import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, AlertCircle, User, Phone, MapPin, DollarSign, FileText, TrendingDown, TrendingUp, Users, Calculator, CreditCard, Network, Calendar, Lock, CheckCircle, Plus } from 'lucide-react';
import { Toast } from './ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import ToastPortal from './ToastPortal';
import LoadingSpinner from './LoadingSpinner';

const ModalModifierFamille = ({ famille, statsGlobales, elevesFamille, interSiteInfo, moisScolaires = [], rapportParMois = [], onClose, onSave }) => {
  // Montant mensuel actuel de la famille (après toutes les réductions)
  const montantNormalApresReductions = statsGlobales?.total_mensuel_avec_reduction || 0;
  const accordFinancierActif = statsGlobales?.accord_financier_actif || null;
  const montantActuel = statsGlobales?.montant_mensuel_effectif ?? accordFinancierActif?.montant_accorde ?? montantNormalApresReductions;
  const nombreEleves = statsGlobales?.nombre_eleves || 0;

  const isInterSite = interSiteInfo?.is_intersite;
  const isReference = interSiteInfo?.is_intersite && interSiteInfo?.is_reference;
  const isDistant = interSiteInfo?.is_intersite && !interSiteInfo?.is_reference;
  // La valeur famille etait modifiable uniquement sur une famille inter-site, et
  // seulement depuis son site de reference. Pour une famille locale - la grande
  // majorite - aucun ecran ne permettait de la corriger : elle se saisissait a la
  // creation et restait figee. C'est l'origine probable des valeurs mortes qu'on
  // a du nettoyer en masse.
  //
  // Elle est desormais modifiable partout, sauf depuis un site distant : la
  // valeur du groupe appartient au site de reference.
  const peutModifierReduction = !isDistant;
  const normalizeStatut = (value) => String(value || '').toLowerCase();
  const moisAccordOptions = (rapportParMois.length > 0 ? rapportParMois : moisScolaires).map((mois) => {
    const moisId = mois.mois_id || mois.id;
    const statut = mois.statut || 'Non paye';
    const totalPaye = Math.round(Number(mois.total_paye || 0));
    const statutNormalise = normalizeStatut(statut);
    const verrouille = totalPaye > 0 || statutNormalise.includes('partiel') || statutNormalise.includes('prorata') || statutNormalise.includes('sold');

    return {
      mois_id: moisId,
      mois_nom: mois.mois_nom || mois.nom,
      ordre: mois.ordre,
      statut,
      total_paye: totalPaye,
      total_du: Math.round(Number(mois.total_du || 0)),
      verrouille
    };
  }).filter((mois) => mois.mois_id);
  const moisEffetParDefaut = moisAccordOptions.find((mois) => !mois.verrouille)?.mois_id || '';

  const [formData, setFormData] = useState({
    nom: famille?.nom || '',
    responsable_nom: famille?.responsable_nom || '',
    telephone: famille?.telephone || '',
    telephone_secondaire: famille?.telephone_secondaire || '',
    adresse: famille?.adresse || '',
    nouveau_montant_mensuel: montantActuel,
    type_repartition: 'automatique_sans_decimales',
    mois_effet_id: moisEffetParDefaut,
    motif_modification: '',
    justificatif_url: '',
    reduction_inscription: parseFloat(famille?.reduction_inscription || 0),
    reduction_mensuel: parseFloat(famille?.reduction_mensuel || 0)
  });

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showSecondPhone, setShowSecondPhone] = useState(Boolean(famille?.telephone_secondaire));

  useEffect(() => {
    if (!formData.mois_effet_id && moisEffetParDefaut) {
      setFormData(prev => ({
        ...prev,
        mois_effet_id: moisEffetParDefaut
      }));
    }
  }, [moisEffetParDefaut, formData.mois_effet_id]);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR').format(montant || 0) + ' FCFA';
  };



  // 🆕 CALCUL DE LA RÉPARTITION AVEC DÉTAILS EXPLICITES
  const calculerRepartitionDetaille = () => {
    if (!elevesFamille || elevesFamille.length === 0) return null;

    const nouveauMontant = parseFloat(formData.nouveau_montant_mensuel) || 0;
    
    if (formData.type_repartition === 'equitable') {
      // ✅ MODE ÉQUITABLE
      const montantParEleve = nouveauMontant / elevesFamille.length;
      
      return {
        mode: 'equitable',
        explication: `Nouveau montant total (${formatMontant(nouveauMontant)}) ÷ Nombre d'élèves (${elevesFamille.length})`,
        formule: `${formatMontant(nouveauMontant)} ÷ ${elevesFamille.length} = ${formatMontant(montantParEleve)} par élève`,
        eleves: elevesFamille.map(eleve => ({
          id: eleve.id,
          nom: eleve.nom,
          prenom: eleve.prenom,
          classe_nom: eleve.classe_nom,
          frais_mensuel_actuel: eleve.frais_mensuel_avec_reduction,
          nouveau_montant: montantParEleve,
          difference: montantParEleve - eleve.frais_mensuel_avec_reduction,
          calcul: `Montant égal pour tous = ${formatMontant(montantParEleve)}`
        }))
      };
    } else {
      // ✅ MODE PROPORTIONNEL
      const totalFraisBase = elevesFamille.reduce((sum, e) => sum + (e.frais_mensuel_normal || 0), 0);
      
      return {
        mode: 'proportionnelle',
        explication: `Répartition selon les frais de base de chaque élève`,
        formule: `Total des frais de base : ${formatMontant(totalFraisBase)}`,
        eleves: elevesFamille.map(eleve => {
          const proportion = totalFraisBase > 0 ? (eleve.frais_mensuel_normal / totalFraisBase) : (1 / elevesFamille.length);
          const pourcentage = (proportion * 100).toFixed(1);
          const nouveauMontantEleve = nouveauMontant * proportion;
          
          return {
            id: eleve.id,
            nom: eleve.nom,
            prenom: eleve.prenom,
            classe_nom: eleve.classe_nom,
            frais_mensuel_actuel: eleve.frais_mensuel_avec_reduction,
            frais_base: eleve.frais_mensuel_normal,
            proportion: pourcentage,
            nouveau_montant: nouveauMontantEleve,
            difference: nouveauMontantEleve - eleve.frais_mensuel_avec_reduction,
            calcul: `${formatMontant(eleve.frais_mensuel_normal)} ÷ ${formatMontant(totalFraisBase)} = ${pourcentage}%
${formatMontant(nouveauMontant)} × ${pourcentage}% = ${formatMontant(nouveauMontantEleve)}`
          };
        })
      };
    }
  };

  const calculerRepartitionAccord = () => {
    if (!elevesFamille || elevesFamille.length === 0) return null;

    const nouveauMontant = Math.max(0, Math.round(parseFloat(formData.nouveau_montant_mensuel) || 0));
    const eleves = elevesFamille.map((eleve, index) => ({
      id: eleve.id,
      nom: eleve.nom,
      prenom: eleve.prenom,
      classe_nom: eleve.classe_nom,
      index,
      frais_mensuel_actuel: Math.max(0, Math.round(parseFloat(eleve.frais_mensuel_avec_reduction) || 0))
    }));
    const totalActuel = eleves.reduce((sum, eleve) => sum + eleve.frais_mensuel_actuel, 0);
    const rawParts = eleves.map((eleve) => {
      const raw = totalActuel > 0
        ? (nouveauMontant * eleve.frais_mensuel_actuel) / totalActuel
        : nouveauMontant / eleves.length;
      const floor = Math.floor(raw);
      return { ...eleve, raw, floor, fraction: raw - floor };
    });

    let reste = nouveauMontant - rawParts.reduce((sum, item) => sum + item.floor, 0);
    const sorted = [...rawParts].sort((a, b) => b.fraction - a.fraction || a.index - b.index);
    const increments = new Map();
    sorted.forEach((item) => {
      if (reste <= 0) return;
      increments.set(item.index, (increments.get(item.index) || 0) + 1);
      reste -= 1;
    });

    return {
      mode: 'automatique_sans_decimales',
      explication: 'Le montant demande est reparti automatiquement selon le poids actuel de chaque eleve, puis ajuste au franc pres.',
      formule: `Total actuel : ${formatMontant(totalActuel)} -> accord demande : ${formatMontant(nouveauMontant)}`,
      eleves: rawParts.map((eleve) => {
        const nouveauMontantEleve = eleve.floor + (increments.get(eleve.index) || 0);
        return {
          id: eleve.id,
          nom: eleve.nom,
          prenom: eleve.prenom,
          classe_nom: eleve.classe_nom,
          frais_mensuel_actuel: eleve.frais_mensuel_actuel,
          nouveau_montant: nouveauMontantEleve,
          difference: nouveauMontantEleve - eleve.frais_mensuel_actuel,
          calcul: `${formatMontant(eleve.frais_mensuel_actuel)} dans le total actuel -> ${formatMontant(nouveauMontantEleve)} apres accord`
        };
      })
    };
  };

  const repartitionDetaille = calculerRepartitionAccord();

  // Calculs automatiques
  const difference = formData.nouveau_montant_mensuel - montantActuel;
  const economieAnnuelle = difference * 9;

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Les messages de ce modal expliquent un circuit - « la demande part au Centre
  // de Support, l'accord sera applique apres approbation ». Un bandeau court les
  // tronquait au milieu. `shouldUseLongToast` arbitre : long au-dela de 130
  // caracteres, ou sur demande explicite.
  const showToast = (type, message, options = {}) => {
    setToast({ type, message, ...options });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.motif_modification.trim()) {
      showToast('error', 'Le motif de modification est obligatoire');
      return;
    }

    // Vérifier qu'il y a une modification
    const montantChange = parseFloat(formData.nouveau_montant_mensuel) !== montantActuel;
    if (montantChange && !formData.mois_effet_id) {
      showToast('error', 'Choisissez le mois d effet de l accord financier');
      return;
    }

    const hasChanges = 
      formData.nom !== famille?.nom ||
      formData.responsable_nom !== (famille?.responsable_nom || '') ||
      formData.telephone !== (famille?.telephone || '') ||
      formData.telephone_secondaire !== (famille?.telephone_secondaire || '') ||
      formData.adresse !== (famille?.adresse || '') ||
      parseFloat(formData.nouveau_montant_mensuel) !== montantActuel ||
      (peutModifierReduction && parseFloat(formData.reduction_inscription) !== parseFloat(famille?.reduction_inscription || 0)) ||
      (peutModifierReduction && parseFloat(formData.reduction_mensuel) !== parseFloat(famille?.reduction_mensuel || 0));

    if (!hasChanges) {
      showToast('error', 'Aucune modification détectée');
      return;
    }

    setLoading(true);
    
    try {
      const success = await onSave(formData);
      
      if (success) {
        showToast('success', 'Modifications enregistrées avec succès !');
        
        // ✅ NE PAS FERMER AUTOMATIQUEMENT - Attendre que l'utilisateur clique sur X
        // onClose(); ← SUPPRIMÉ
      } else {
        showToast('error', 'Erreur lors de l\'enregistrement des modifications');
      }
    } catch (error) {
      showToast('error', 'Erreur lors de l\'enregistrement des modifications');
    } finally {
      setLoading(false);
    }
  };

  
return createPortal(
    <>
      {/* Hors du Dialog : Radix rend son contenu dans son propre portail, et un
          toast laisse a l'interieur resterait prisonnier de sa pile. */}
      {toast && (
        <ToastPortal>
          {shouldUseLongToast(toast.message, toast.type, toast) ? (
            <LongToast
              show
              title={toast.title}
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          ) : (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}
        </ToastPortal>
      )}

      {loading && <LoadingSpinner />}

      <div 
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4"
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 10000
        }}
      >
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto" style={{ zIndex: 10001 }}>
          {/* Header */}
          <div className="bg-[#246990] px-5 py-3 rounded-t-lg flex items-center justify-between sticky top-0 z-10">
            <div>
              <h2 className="text-lg font-semibold text-white">
                Modifier les informations - {famille?.nom}
              </h2>
              <p className="text-white/80 text-sm mt-0.5">
                Modification des coordonnées et montant mensuel
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors disabled:opacity-50"
            >
              <X size={20} />
            </button>
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="p-5">
            <div className="grid grid-cols-12 gap-5">
              {/* ========== COLONNE GAUCHE ========== */}
              <div className="col-span-5 space-y-4">
                {/* Informations générales */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-300">
                    <div className="w-7 h-7 bg-[#246990] rounded-lg flex items-center justify-center">
                      <User size={14} className="text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Informations générales</h3>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Nom de la famille
                      </label>
                      <input
                        type="text"
                        value={formData.nom}
                        onChange={(e) => handleChange('nom', e.target.value)}
                        disabled={loading}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent disabled:bg-gray-100"
                        placeholder="Ex: DIALOKA Divin"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                        <User size={12} className="text-gray-500" />
                        Responsable de la famille
                      </label>
                      <input
                        type="text"
                        value={formData.responsable_nom}
                        onChange={(e) => handleChange('responsable_nom', e.target.value)}
                        disabled={loading}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent disabled:bg-gray-100"
                        placeholder="Nom du parent ou tuteur responsable"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                          <Phone size={12} className="text-gray-500" />
                          Téléphones parent / responsable
                        </label>
                        {!showSecondPhone && (
                          <button
                            type="button"
                            onClick={() => setShowSecondPhone(true)}
                            disabled={loading}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-[#246990] hover:text-[#1b5576] disabled:opacity-50"
                          >
                            <Plus size={12} />
                            Ajouter
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        <input
                          type="tel"
                          value={formData.telephone}
                          onChange={(e) => handleChange('telephone', e.target.value)}
                          disabled={loading}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent disabled:bg-gray-100"
                          placeholder="Numéro principal"
                        />
                        {showSecondPhone && (
                          <div className="flex items-center gap-2">
                            <input
                              type="tel"
                              value={formData.telephone_secondaire}
                              onChange={(e) => handleChange('telephone_secondaire', e.target.value)}
                              disabled={loading}
                              className="min-w-0 flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent disabled:bg-gray-100"
                              placeholder="Numéro secondaire"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                handleChange('telephone_secondaire', '');
                                setShowSecondPhone(false);
                              }}
                              disabled={loading}
                              className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                              title="Retirer le second numéro"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-gray-500">
                        Deux numéros maximum. Chaque numéro reste dans son champ, sans slash.
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                        <MapPin size={12} className="text-gray-500" />
                        Adresse
                      </label>
                      <textarea
                        value={formData.adresse}
                        onChange={(e) => handleChange('adresse', e.target.value)}
                        disabled={loading}
                        rows="3"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent resize-none disabled:bg-gray-100"
                        placeholder="Ex: Quartier Moungali, Rue..."
                      />
                    </div>
                  </div>
                </div>

                

                {/* Réduction Famille Globale (inter-site) */}
                {(
                  <div className={`rounded-lg p-4 border ${peutModifierReduction ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-300">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${peutModifierReduction ? 'bg-teal-600' : 'bg-gray-500'}`}>
                        <Network size={14} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold text-sm ${peutModifierReduction ? 'text-teal-900' : 'text-gray-700'}`}>
                          {isInterSite ? 'Réduction famille inter-site' : 'Valeur famille'}
                        </h3>
                        <p className="text-[10px] text-gray-500">
                          {isInterSite
                            ? 'Réduction de référence, propagée aux élèves qui suivent la valeur famille'
                            : 'Réduction reprise par les élèves qui suivent la valeur famille. Ceux qui ont leur propre réduction ne sont pas concernés.'}
                        </p>
                      </div>
                    </div>

                    {isDistant && (
                      <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 mb-3">
                        <AlertCircle size={12} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-[10px] text-amber-700">
                          Ces reductions sont definies et gerees par le <strong>site de reference</strong>. Vous ne pouvez pas les modifier depuis ce site.
                        </p>
                      </div>
                    )}

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Réduction inscription
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            step="100"
                            value={formData.reduction_inscription}
                            onChange={(e) => handleChange('reduction_inscription', parseFloat(e.target.value) || 0)}
                            disabled={loading || isDistant}
                            className={`w-full px-3 py-2 text-sm border rounded-lg pr-8 ${
                              isDistant 
                                ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed' 
                                : 'border-teal-300 focus:ring-2 focus:ring-teal-600 focus:border-transparent bg-white'
                            }`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">F</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Réduction mensuelle
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            step="100"
                            value={formData.reduction_mensuel}
                            onChange={(e) => handleChange('reduction_mensuel', parseFloat(e.target.value) || 0)}
                            disabled={loading || isDistant}
                            className={`w-full px-3 py-2 text-sm border rounded-lg pr-8 ${
                              isDistant 
                                ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed' 
                                : 'border-teal-300 focus:ring-2 focus:ring-teal-600 focus:border-transparent bg-white'
                            }`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">F</span>
                        </div>
                      </div>
                    </div>

                    {isReference && (
                      <div className="mt-3 p-2 bg-teal-100 border border-teal-300 rounded flex items-start gap-2">
                        <AlertCircle className="text-teal-700 flex-shrink-0 mt-0.5" size={14} />
                        <p className="text-xs text-teal-800">
                          <strong>Site de reference :</strong> les reductions famille sont propagees vers les sites distants. Les accords financiers restent locaux a ce site.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Montant Mensuel */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-blue-300">

                 <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
                      <CreditCard size={14} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 text-sm">
                        {isInterSite ? 'Montant Mensuel - Élèves de ce site' : 'Montant Mensuel de la Famille'}
                      </h3>
                      {isInterSite && (
                        <p className="text-[10px] text-blue-600">Concerne uniquement vos {nombreEleves} élève{nombreEleves > 1 ? 's' : ''} local{nombreEleves > 1 ? 'ux' : ''}</p>
                      )}
                    </div>


                  </div>

                  <div className="space-y-3">
                    {isInterSite && (
                      <div className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs text-blue-800">
                        <p className="font-semibold">Accord financier local</p>
                        <p className="mt-1 text-[11px] leading-snug text-blue-700">
                          Le nouveau montant ne concerne que les eleves de ce site. Les membres distants gardent leur propre dossier de paiement.
                        </p>
                      </div>
                    )}

                    {/* Montant actuel */}
                    <div className="bg-white rounded-lg p-3 border border-blue-300">
                      <div className="text-xs text-blue-700 mb-1">
                        Montant actuel à payer
                      </div>
                      <div className="text-2xl font-bold text-blue-900">
                        {formatMontant(montantActuel)}
                      </div>
                      <div className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                        <Users size={12} />
                        Pour {nombreEleves} élève{nombreEleves > 1 ? 's' : ''}
                      </div>
                    </div>

                    {accordFinancierActif && (
                      <div className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs text-blue-800">
                        <div className="flex items-center justify-between gap-2">
                          <span>Base avant accord financier</span>
                          <strong>{formatMontant(montantNormalApresReductions)}</strong>
                        </div>
                        <p className="mt-1 text-[11px] leading-snug text-blue-600">
                          Montant calculé après les réductions individuelles, avant l'accord actif.
                        </p>
                      </div>
                    )}

                    {/* Nouveau montant */}
                    <div>
                      <label className="block text-xs font-medium text-blue-900 mb-1.5">
                        Nouveau montant mensuel <span className="text-red-600">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="100"
                          value={formData.nouveau_montant_mensuel}
                          onChange={(e) => handleChange('nouveau_montant_mensuel', parseFloat(e.target.value) || 0)}
                          disabled={loading}
                          className="w-full px-3 py-2 text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white pr-8 disabled:bg-gray-100"
                          placeholder="Ex: 20000"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">F</span>
                      </div>
                    </div>

                    {parseFloat(formData.nouveau_montant_mensuel) !== montantActuel && (
                      <div className="rounded-lg border border-blue-200 bg-white p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar size={14} className="text-blue-700" />
                          <div>
                            <div className="text-xs font-semibold text-blue-900">Mois d'effet de l'accord</div>
                            <p className="text-[11px] text-blue-700">
                              Par defaut, l'accord commence au premier mois non paye. Les mois payes ou partiels gardent leur ancien montant.
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto pr-1">
                          {moisAccordOptions.map((mois) => {
                            const selected = Number(formData.mois_effet_id) === Number(mois.mois_id);
                            return (
                              <button
                                key={mois.mois_id}
                                type="button"
                                onClick={() => !mois.verrouille && handleChange('mois_effet_id', mois.mois_id)}
                                disabled={loading || mois.verrouille}
                                className={`text-left rounded-lg border px-3 py-2 transition-colors ${
                                  selected
                                    ? 'border-[#246990] bg-[#246990]/10 text-[#246990]'
                                    : mois.verrouille
                                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                      : 'border-blue-100 bg-white text-gray-700 hover:border-[#246990]'
                                }`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-semibold">{mois.mois_nom}</span>
                                  {mois.verrouille ? (
                                    <Lock size={13} />
                                  ) : selected ? (
                                    <CheckCircle size={13} />
                                  ) : null}
                                </div>
                                <div className="mt-1 text-[10px]">
                                  {mois.verrouille
                                    ? `Conserve (${mois.statut}${mois.total_paye ? ` - ${formatMontant(mois.total_paye)}` : ''})`
                                    : 'Applicable'}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Type de répartition */}
                    <div>
                      <label className="block text-xs font-medium text-blue-900 mb-2">
                        Répartition
                      </label>
                      <div className="space-y-2">
                        <div className="rounded-lg border border-blue-200 bg-white p-3 text-xs text-blue-800">
                          <div className="font-semibold text-blue-900">Automatique sans décimales</div>
                          <p className="mt-1">
                            Le backend répartira le montant accordé au franc près sur les élèves.
                            Les réductions existantes resteront intactes.
                          </p>
                        </div>
                        <div className="hidden">
                        <label className="flex items-start gap-2 p-2 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                          <input
                            type="radio"
                            name="type_repartition"
                            value="equitable"
                            checked={formData.type_repartition === 'equitable'}
                            onChange={(e) => handleChange('type_repartition', e.target.value)}
                            disabled={loading}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="text-xs font-medium text-blue-900">Équitable</div>
                            <div className="text-xs text-blue-700">Montant égal pour chaque élève</div>
                          </div>
                        </label>
                        <label className="flex items-start gap-2 p-2 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                          <input
                            type="radio"
                            name="type_repartition"
                            value="proportionnelle"
                            checked={formData.type_repartition === 'proportionnelle'}
                            onChange={(e) => handleChange('type_repartition', e.target.value)}
                            disabled={loading}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="text-xs font-medium text-blue-900">Proportionnelle</div>
                            <div className="text-xs text-blue-700">Selon les frais de base de chaque élève</div>
                          </div>
                        </label>
                        </div>
                      </div>
                    </div>

                    {/* Impact */}
                    {difference !== 0 && (
                      <div className={`p-3 rounded-lg border ${
                        difference < 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          {difference < 0 ? (
                            <>
                              <TrendingDown size={14} className="text-green-600" />
                              <span className="text-xs font-semibold text-green-900">Économie</span>
                            </>
                          ) : (
                            <>
                              <TrendingUp size={14} className="text-red-600" />
                              <span className="text-xs font-semibold text-red-900">Augmentation</span>
                            </>
                          )}
                        </div>
                        <div className="space-y-1 text-xs">
                          <div className={`font-bold text-sm ${difference < 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {difference < 0 ? '-' : '+'} {formatMontant(Math.abs(difference))}/mois
                          </div>
                          <div className={difference < 0 ? 'text-green-600' : 'text-red-600'}>
                            {difference < 0 ? '- ' : '+ '}{formatMontant(Math.abs(economieAnnuelle))}/an
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 p-2 bg-blue-100 border border-blue-300 rounded flex items-start gap-2">
                    <AlertCircle className="text-blue-700 flex-shrink-0 mt-0.5" size={14} />
                    <p className="text-xs text-blue-800">
                      <strong>Important :</strong> Cette demande creera un <strong>accord financier famille</strong> apres validation. Les reductions eleves ne seront pas modifiees. Les mois deja payes ou partiellement payes resteront sur leur ancien montant.
                    </p>
                  </div>
                </div>
              </div>

            {/* ========== COLONNE DROITE ========== */}
              <div className="col-span-7 space-y-4">
                {/* 🆕 CALCUL DÉTAILLÉ DE LA RÉPARTITION */}
                {repartitionDetaille && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-green-300">
                      <div className="w-7 h-7 bg-green-600 rounded-lg flex items-center justify-center">
                        <Calculator size={14} className="text-white" />
                      </div>
                      <h3 className="font-semibold text-green-900 text-sm">
                        Répartition automatique sans décimales
                      </h3>
                    </div>

                    {/* Explication générale */}
                    <div className="mb-3 p-2 bg-green-100 border border-green-300 rounded">
                      <div className="text-xs font-medium text-green-900 mb-1">
                        {repartitionDetaille.explication}
                      </div>
                      <div className="text-xs text-green-700 font-mono">
                        {repartitionDetaille.formule}
                      </div>
                    </div>

                    {/* Détail par élève */}
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {repartitionDetaille.eleves.map((eleve, index) => (
                        <div 
                          key={eleve.id} 
                          className={`p-3 rounded border ${
                            index % 2 === 0 ? 'bg-white border-green-200' : 'bg-green-50 border-green-100'
                          }`}
                        >
                          <div className="space-y-2 pr-1">
                            <div className="grid grid-cols-[minmax(0,1fr)_96px_112px] items-center gap-2">
                              <div className="min-w-0 text-xs font-semibold text-gray-900 truncate">
                                {eleve.prenom} {eleve.nom}
                              </div>
                              <span className="inline-flex h-6 w-[96px] items-center justify-center rounded-md bg-blue-100 px-2 text-[10px] font-medium text-blue-700">
                                <span className="truncate">{eleve.classe_nom}</span>
                              </span>
                              <span
                                title={formatMontant(eleve.difference)}
                                className={`inline-flex h-7 w-[112px] items-center justify-center whitespace-nowrap rounded-md px-2 text-[10px] font-semibold ${
                                eleve.difference < 0
                                  ? 'bg-green-100 text-green-700'
                                  : eleve.difference > 0
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                              >
                                {eleve.difference < 0 ? '' : '+'}{formatMontant(eleve.difference)}
                              </span>
                            </div>

                              {/* Calcul détaillé */}
                              <div className="bg-gray-50 p-2 rounded border border-gray-200 mb-2">
                                <div className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                                  <Calculator size={10} />
                                  Calcul :
                                </div>
                                <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap">
                                  {eleve.calcul}
                                </pre>
                              </div>

                              {/* Résultat */}
                              <div className="flex items-center gap-2 text-xs pt-2 border-t border-gray-200">
                                <span className="text-gray-500 line-through">
                                  {formatMontant(eleve.frais_mensuel_actuel)}
                                </span>
                                <span className="text-gray-400">→</span>
                                <span className="font-bold text-green-700 text-sm">
                                  {formatMontant(eleve.nouveau_montant)}
                                </span>
                              </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Justification */}
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-orange-300">
                    <div className="w-7 h-7 bg-orange-600 rounded-lg flex items-center justify-center">
                      <FileText size={14} className="text-white" />
                    </div>
                    <h3 className="font-semibold text-orange-900 text-sm">Justification de la modification</h3>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-orange-900 mb-1.5">
                      Motif de la modification <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      value={formData.motif_modification}
                      onChange={(e) => handleChange('motif_modification', e.target.value)}
                      disabled={loading}
                      rows="6"
                      required
                      className="w-full px-3 py-2 text-sm border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent resize-none bg-white disabled:bg-gray-100"
                      placeholder="Ex: Ajout d'un 5ème enfant dans la fratrie, difficulté financière temporaire..."
                    />
                  </div>
                </div>

                {/* Résumé des modifications */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="text-gray-600" size={16} />
                    <h3 className="font-semibold text-gray-900 text-sm">Résumé des modifications</h3>
                  </div>
                  
                  <div className="space-y-2 text-xs">
                    {formData.nom !== famille?.nom && (
                      <div className="flex justify-between py-1.5 border-b border-gray-200">
                        <span className="text-gray-700">Nom :</span>
                        <span className="font-medium text-gray-900">
                          <span className="line-through text-gray-500">{famille?.nom}</span> → {formData.nom}
                        </span>
                      </div>
                    )}

                    {formData.responsable_nom !== (famille?.responsable_nom || '') && (
                      <div className="flex justify-between py-1.5 border-b border-gray-200">
                        <span className="text-gray-700">Responsable :</span>
                        <span className="font-medium text-gray-900">
                          <span className="line-through text-gray-500">{famille?.responsable_nom || 'Aucun'}</span> → {formData.responsable_nom || 'Aucun'}
                        </span>
                      </div>
                    )}
                    
                    {formData.telephone !== (famille?.telephone || '') && (
                      <div className="flex justify-between py-1.5 border-b border-gray-200">
                        <span className="text-gray-700">Téléphone :</span>
                        <span className="font-medium text-gray-900">
                          <span className="line-through text-gray-500">{famille?.telephone || 'Aucun'}</span> → {formData.telephone}
                        </span>
                      </div>
                    )}

                    {formData.telephone_secondaire !== (famille?.telephone_secondaire || '') && (
                      <div className="flex justify-between py-1.5 border-b border-gray-200">
                        <span className="text-gray-700">Téléphone 2 :</span>
                        <span className="font-medium text-gray-900">
                          <span className="line-through text-gray-500">{famille?.telephone_secondaire || 'Aucun'}</span> → {formData.telephone_secondaire || 'Aucun'}
                        </span>
                      </div>
                    )}
                    
                    {formData.adresse !== (famille?.adresse || '') && (
                      <div className="flex justify-between py-1.5 border-b border-gray-200">
                        <span className="text-gray-700">Adresse :</span>
                        <span className="font-medium text-gray-900">Modifiée</span>
                      </div>
                    )}

                    {peutModifierReduction && parseFloat(formData.reduction_inscription) !== parseFloat(famille?.reduction_inscription || 0) && (
                      <div className="flex justify-between py-1.5 border-b border-gray-200">
                        <span className="text-gray-700">Réd. inscription (inter-site) :</span>
                        <span className="font-medium text-gray-900">
                          <span className="line-through text-gray-500">{formatMontant(famille?.reduction_inscription)}</span> → {formatMontant(formData.reduction_inscription)}
                        </span>
                      </div>
                    )}

                    {peutModifierReduction && parseFloat(formData.reduction_mensuel) !== parseFloat(famille?.reduction_mensuel || 0) && (
                      <div className="flex justify-between py-1.5 border-b border-gray-200">
                        <span className="text-gray-700">Réd. mensuelle (inter-site) :</span>
                        <span className="font-medium text-gray-900">
                          <span className="line-through text-gray-500">{formatMontant(famille?.reduction_mensuel)}</span> → {formatMontant(formData.reduction_mensuel)}
                        </span>
                      </div>
                    )}
                    
                    {parseFloat(formData.nouveau_montant_mensuel) !== montantActuel && (
                      <div className="flex justify-between py-1.5 border-b border-gray-200">
                        <span className="text-gray-700">Accord financier demande :</span>
                        <span className="font-medium text-gray-900">
                          <span className="line-through text-gray-500">
                            {formatMontant(montantActuel)}
                          </span> → {formatMontant(formData.nouveau_montant_mensuel)}
                        </span>
                      </div>
                    )}

                    {/* 🆕 MODE DE RÉPARTITION */}
                    {parseFloat(formData.nouveau_montant_mensuel) !== montantActuel && (
                      <div className="flex justify-between py-1.5 border-b border-gray-200">
                        <span className="text-gray-700">Répartition :</span>
                        <span className="font-medium text-gray-900">
                          Automatique sans décimales
                        </span>
                      </div>
                    )}

                    {parseFloat(formData.nouveau_montant_mensuel) !== montantActuel && (
                      <div className="flex justify-between py-1.5 border-b border-gray-200">
                        <span className="text-gray-700">Mois d'effet :</span>
                        <span className="font-medium text-gray-900">
                          {moisAccordOptions.find((mois) => Number(mois.mois_id) === Number(formData.mois_effet_id))?.mois_nom || '-'}
                        </span>
                      </div>
                    )}

                  {formData.nom === famille?.nom && 
                     formData.responsable_nom === (famille?.responsable_nom || '') &&
                     formData.telephone === (famille?.telephone || '') && 
                     formData.telephone_secondaire === (famille?.telephone_secondaire || '') &&
                     formData.adresse === (famille?.adresse || '') &&
                     parseFloat(formData.nouveau_montant_mensuel) === montantActuel &&
                     (!peutModifierReduction || (
                       parseFloat(formData.reduction_inscription) === parseFloat(famille?.reduction_inscription || 0) &&
                       parseFloat(formData.reduction_mensuel) === parseFloat(famille?.reduction_mensuel || 0)
                     )) && (
                      <p className="text-gray-600 text-center py-2">Aucune modification détectée</p>
                    )}

                  </div>
                </div>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="bg-gray-50 px-5 py-3 rounded-b-lg border-t border-gray-200 flex justify-end gap-3 sticky bottom-0">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 text-sm bg-[#246990] text-white rounded-lg hover:bg-[#1d5475] transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  Envoi de la demande...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Envoyer la demande
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ModalModifierFamille;

